import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import StoreLayout from "@/components/store/StoreLayout";
import Home from "@/pages/store/Home";
import About from "@/pages/store/About";
import Products from "@/pages/store/Products";
import ProductDetail from "@/pages/store/ProductDetail";
import Checkout from "@/pages/store/Checkout";
import OrderSuccess from "@/pages/store/OrderSuccess";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import AccountLayout from "@/pages/account/AccountLayout";
import MyOrders from "@/pages/account/MyOrders";
import Addresses from "@/pages/account/Addresses";
import Profile from "@/pages/account/Profile";
import MyInvoices from "@/pages/account/MyInvoices";
import PanelLayout from "@/components/panel/PanelLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import SalesLedger from "@/pages/admin/SalesLedger";
import Users from "@/pages/admin/Users";
import Profiles from "@/pages/admin/Profiles";
import Stock from "@/pages/admin/Stock";
import Fiscal from "@/pages/admin/Fiscal";
import Finance from "@/pages/admin/Finance";
import CRM from "@/pages/admin/CRM";
import Sellers from "@/pages/admin/Sellers";
import Coupons from "@/pages/admin/Coupons";
import Banners from "@/pages/admin/Banners";
import Rentals from "@/pages/admin/Rentals";
import Abandoned from "@/pages/admin/Abandoned";
import Reviews from "@/pages/admin/Reviews";
import Reports from "@/pages/admin/Reports";
import SettingsPage from "@/pages/admin/Settings";
import AdminChangeRequests from "@/pages/admin/ChangeRequests";
import Suppliers from "@/pages/admin/Suppliers";
import Categories from "@/pages/admin/Categories";
import StockImport from "@/pages/admin/StockImport";
import ServiceOrders from "@/pages/admin/ServiceOrders";
import Agenda from "@/pages/admin/Agenda";
import POS from "@/pages/seller/POS";
import Commissions from "@/pages/seller/Commissions";
import SellerCustomers from "@/pages/seller/SellerCustomers";
import ChangeRequests from "@/pages/seller/ChangeRequests";
import CashRegister from "@/pages/seller/CashRegister";
import NotFound from "@/pages/NotFound";
import { SCREENS, PANEL_BASE, PANEL_HOME, LEGACY_REDIRECTS } from "@/components/panel/screens";

/**
 * Tela fora do perfil. Mostra o aviso em vez de redirecionar: como a própria
 * tela inicial do painel também é permissionável, um redirecionamento poderia
 * cair em laço quando o perfil não tem nem o destino.
 */
function NoAccess() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-8 text-center" data-testid="no-access">
      <p className="font-heading text-lg font-semibold text-slate-900">Você não tem acesso a esta tela</p>
      <p className="text-sm text-slate-500 mt-1">Seu perfil de acesso não inclui esta página. Use o menu ao lado ou peça a liberação ao administrador.</p>
    </div>
  );
}

function Protected({ roles, permission, children }) {
  const { user, loading, can } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-500">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (permission && !can(permission)) return <NoAccess />;
  return children;
}

/** Envolve a tela do painel na checagem de permissão (rota + menu usam a mesma chave). */
const P = (permission, element) => <Protected permission={permission}>{element}</Protected>;

/**
 * Componente de cada tela, por chave de permissão.
 *
 * Uma função quando a tela muda com o painel: em Solicitações o admin aprova
 * (`AdminChangeRequests`) e quem está no balcão abre o pedido (`ChangeRequests`).
 */
const SCREEN_ELEMENTS = {
  dashboard: <AdminDashboard />,
  relatorios: <Reports />,
  pedidos: <AdminOrders />,
  produtos: <AdminProducts />,
  categorias: <Categories />,
  fornecedores: <Suppliers />,
  estoque: <Stock />,
  "importar-xml": <StockImport />,
  solicitacoes: (role) => (role === "admin" ? <AdminChangeRequests /> : <ChangeRequests />),
  "ordens-servico": <ServiceOrders />,
  agenda: <Agenda />,
  alugueis: <Rentals />,
  carrinhos: <Abandoned />,
  pdv: <POS />,
  vendas: <SalesLedger />,
  "frente-caixa": <CashRegister />,
  comissoes: <Commissions />,
  "clientes-atendidos": <SellerCustomers />,
  financeiro: <Finance />,
  fiscal: <Fiscal />,
  cupons: <Coupons />,
  banners: <Banners />,
  crm: <CRM />,
  vendedores: <Sellers />,
  avaliacoes: <Reviews />,
  usuarios: <Users />,
  perfis: <Profiles />,
  configuracoes: <SettingsPage />,
};

/**
 * As rotas de um painel, do mesmo catálogo que monta o menu.
 *
 * Antes cada painel tinha a própria lista escrita à mão, e a do vendedor
 * conhecia 8 telas das 29 — liberar uma tela no perfil não abria nada porque a
 * rota não existia. Com uma fonte só, conceder passa a conceder de verdade, e a
 * permissão volta a ser a única trava (`Protected permission=`).
 */
function panelRoutes(role) {
  const rotas = SCREENS.map((screen) => {
    const el = SCREEN_ELEMENTS[screen.perm];
    const element = P(screen.perm, typeof el === "function" ? el(role) : el);

    return screen.perm === PANEL_HOME[role]
      ? <Route key={screen.perm} index element={element} />
      : <Route key={screen.perm} path={screen.path} element={element} />;
  });

  // Endereços antigos do painel (ex.: /vendedor/estoque) continuam válidos:
  // link salvo e atalho na tela inicial do tablet não podem quebrar.
  for (const [antigo, novo] of Object.entries(LEGACY_REDIRECTS[role] || {})) {
    rotas.push(<Route key={`legacy-${antigo}`} path={antigo} element={<Navigate to={`${PANEL_BASE[role]}/${novo}`} replace />} />);
  }

  rotas.push(<Route key="404" path="*" element={<NotFound variant="panel" />} />);

  return rotas;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<StoreLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/sobre" element={<About />} />
                <Route path="/produtos" element={<Products />} />
                <Route path="/produto/:slug" element={<ProductDetail />} />
                <Route path="/checkout" element={<Protected roles={["cliente", "admin", "vendedor"]}><Checkout /></Protected>} />
                <Route path="/pedido-confirmado" element={<OrderSuccess />} />
                <Route path="/conta" element={<Protected roles={["cliente", "admin", "vendedor"]}><AccountLayout /></Protected>}>
                  <Route path="pedidos" element={<MyOrders />} />
                  <Route path="enderecos" element={<Addresses />} />
                  <Route path="dados" element={<Profile />} />
                  <Route path="notas" element={<MyInvoices />} />
                </Route>
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Register />} />
              {/* Os três painéis servem as MESMAS telas, do mesmo catálogo. O
                  que cada pessoa vê é decidido pela permissão do perfil, tanto
                  no menu quanto na rota — e o servidor confere a mesma chave. */}
              <Route path="/admin" element={<Protected roles={["admin"]}><PanelLayout role="admin" /></Protected>}>
                {panelRoutes("admin")}
              </Route>
              <Route path="/vendedor" element={<Protected roles={["vendedor", "admin"]}><PanelLayout role="vendedor" /></Protected>}>
                {panelRoutes("vendedor")}
              </Route>
              <Route path="/balcao" element={<Protected roles={["balcao", "vendedor", "admin"]}><PanelLayout role="balcao" /></Protected>}>
                {panelRoutes("balcao")}
              </Route>
              {/* Último a casar: qualquer endereço fora das rotas acima. */}
              <Route element={<StoreLayout />}>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </CartProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
